import { ModalType } from '../enums';
import { useEffect, useState } from 'react';
import { verifyEmail } from '../components/modal/verifyemail';
import { registerLogin } from '../components/modal/registerlogin';
import { quoteSaved } from '../components/modal/quotesaved';
import { useLocation } from 'react-router-dom';
import { canScroll } from '../store/reducers/layout';
import { useAppDispatch, useAppSelector } from './statehooks';
import { ModalContent, ModalSettings, TextContent } from 'interface';
import { invalidQuote } from '@components/modal/invalidquote';
import { bchQualify } from '@components/modal/bchqualify';
import { nonQuotable } from '@components/modal/nonquotable';

const useModal = () => {
  const { search } = useLocation();
  const make = useAppSelector((state) => state.vehicleInformation.make);

  const dispatch = useAppDispatch();

  const [visible, setVisible] = useState(false);
  const [modalType, setModalType] = useState<ModalType>();
  const [buttons, setButtons] = useState<JSX.Element>();
  const [content, setContent] = useState<ModalContent | null>(null);
  const [textContent, setTextContent] = useState<TextContent | null>(null);
  const [className, setClassName] = useState<string>();

  const auth = useAppSelector((state) => {
    return state.vehicleInformation.siteCoreDetails?.config?.auth0Config
      ?.auth0Enabled;
  }) as boolean;

  function updateContent(modalContent: ModalContent): void {
    setContent({
      icon: modalContent.icon,
      title: modalContent.title,
      message: modalContent.message,
      buttons: buttons ?? modalContent.buttons,
    });
  }

  const toggle = (s: any) => {
    if (s && !s.target.classList.contains('lightbox')) {
      return;
    }
    dispatch(canScroll(visible));
    setVisible(!visible);
  };

  const closeModal = () => {
    dispatch(canScroll(true));
    setVisible(false);
  };

  useEffect(() => {
    if (auth) {
      setTextContent({
        shortlistTitle: 'Save quote to shortlist',
        shortlistMessage:
          'You need to be logged in to save quotes to your shortlist',
        startOrderTitle: "You'll need to register or log in",
        startOrderMessage: 'To continue with your order',
      });
    } else {
      setTextContent({
        shortlistTitle: "You'll need to register or log in",
        shortlistMessage: 'To save that to your shortlist',
        startOrderTitle: "You'll need to register or log in",
        startOrderMessage: 'To continue with your order',
      });
    }
  }, [auth]);

  useEffect(() => {
    switch (modalType) {
      case ModalType.verifyEmail:
        updateContent(verifyEmail(search));
        break;
      case ModalType.registerLoginShortlist:
        updateContent(
          registerLogin(
            search,
            textContent?.shortlistTitle as string,
            textContent?.shortlistMessage as string,
            auth
          )
        );
        break;
      case ModalType.registerLoginStartOrder:
        updateContent(
          registerLogin(
            search,
            textContent?.startOrderTitle as string,
            textContent?.startOrderMessage as string,
            auth
          )
        );
        break;
      case ModalType.quoteSaved:
        updateContent(quoteSaved());
        break;
      case ModalType.invalidQuote:
        updateContent(invalidQuote(closeModal));
        break;
      case ModalType.bchQualify:
        updateContent(bchQualify());
        break;
      case ModalType.nonQuotable:
        updateContent(nonQuotable(make));
        break;
    }
  }, [modalType, buttons, className]);

  return {
    toggle: toggle,
    visible: visible,
    setModalType: setModalType,
    content: content,
    modalType: modalType,
    setButtons: setButtons,
    closeModal: closeModal,
    setClassName: setClassName,
    className: className,
  } as ModalSettings;
};

export default useModal;
